import { Filter } from '@fabric-ui/components';
import { defaultDrillDownHierarchy, NO_VALUE, OTHERS } from '../../constants';
import { AllTypeWidgets, WidgetCategory } from '../../types';
import { BarVisualizationType, getBarQueryType } from '../Widgets/BarWidget/BarWidget.utils';
import { useDrillDownData } from './useDrillDownData';

export const useDrillDown = ({
  widget,
  setWidget,
  enable = false,
  contextFilter,
}: {
  widget: AllTypeWidgets;
  setWidget: ((w) => void) | undefined;
  enable: boolean;
  contextFilter: Filter | undefined;
}) => {
  const {
    drillDownHierarchy = defaultDrillDownHierarchy,
    requests: [
      {
        select: { dims },
      },
    ],
  } = widget;
  const { activeIndex, drillDownValues = [] } = drillDownHierarchy;

  const {
    data: drillDownData,
    isLoadingDrillDown,
    totalsDrillDown,
    allDrillDownFields,
    newQueryObject,
  } = useDrillDownData({
    widget,
    contextFilter,
    enabled: activeIndex !== 0 && enable,
  });

  const dim = activeIndex !== 0 ? allDrillDownFields[activeIndex].name : dims?.[0]?.name;
  const nextDrillDownField = allDrillDownFields[activeIndex + 1];

  const getValue = rawObj => {
    const obj = rawObj.d || rawObj;

    if (widget.category === WidgetCategory.Pie) {
      return obj[dim];
    }

    if (widget.category === WidgetCategory.Bar) {
      if (
        getBarQueryType(widget.requests[0].select) === BarVisualizationType.MultiMetric &&
        ('activePayload' in obj || 'activeLabel' in obj)
      ) {
        return getBarMultiMeasurementsDrillDownValues(obj);
      }
      return obj.originalValue ?? obj.name;
    }

    return obj;
  };

  const getPressedValue = data => {
    const value = getValue(data);
    if (widget.category === WidgetCategory.Pie || widget.category === WidgetCategory.Bar) {
      return value === NO_VALUE ? null : value;
    }
    return data;
  };

  const getNotOthersDrillDownValues = (value, data) => {
    if (value !== OTHERS) return [];
    if ('widgetData' in data) {
      return data.widgetData.filter(obj => getValue(obj) !== 'Others').map(obj => getValue(obj));
    }

    if ('notOthers' in data) {
      return data.notOthers;
    }
    return [];
  };

  const onSelectedDrillDown = data => {
    if (setWidget && nextDrillDownField) {
      const value = getPressedValue(data);
      setWidget({
        ...widget,
        drillDownHierarchy: {
          ...drillDownHierarchy,
          activeIndex: activeIndex + 1,
          drillDownValues: drillDownValues[activeIndex] ? drillDownValues.with(activeIndex, value) : [...drillDownValues, value],
          notOthersDrillDownValues: getNotOthersDrillDownValues(value, data),
        },
      });
    }
  };

  return { drillDownData, totalsDrillDown, isLoadingDrillDown, allDrillDownFields, activeIndex, onSelectedDrillDown, newQueryObject };
};

const getBarMultiMeasurementsDrillDownValues = (payload: {
  activeTooltipIndex: number;
  activeLabel: string;
  activePayload: {
    name: string;
    dataKey: string | number;
    value: string | number;
    payload: Record<string, unknown> & { headCellType?: string; originalValue?: string };
  }[];
}) => {
  const originalValue = payload.activePayload.at(0)?.payload.originalValue;
  return originalValue || payload.activeLabel;
};
